export const mockData = {
  hero: {
    headline: "I Build AI-Powered Systems That Save Businesses Time & Money",
    subheading: "Transforming business operations with intelligent automation, modern web solutions, and scalable backend systems",
    trustLine: "Trusted by enterprises | Ex-KPMG Global Services",
    ctas: [
      { text: "Get a Free Consultation", primary: true },
      { text: "View My Work", primary: false }
    ]
  },
  
  services: [
    {
      id: 1,
      title: "Website Development",
      description: "Fast, modern, SEO-ready websites that convert visitors into customers",
      problem: "Slow, outdated websites that don't generate leads",
      solution: "Modern, responsive web applications built with React, optimized for speed and conversion",
      outcome: "3x faster load times, 50% increase in user engagement",
      features: ["React Development", "Responsive Design", "SEO Optimization", "Performance Focused"]
    },
    {
      id: 2,
      title: "AI Automation",
      description: "Intelligent chatbots, workflow automation, and seamless integrations that work 24/7",
      problem: "Manual, repetitive tasks consuming valuable team time",
      solution: "Custom AI solutions that automate workflows, respond to customers instantly, and integrate with your existing tools",
      outcome: "Save 20+ hours/week and reduce operational costs by 40%",
      features: ["Custom AI Chatbots", "Workflow Automation", "API Integrations", "Process Optimization"]
    },
    {
      id: 3,
      title: "Internal Tools",
      description: "Custom dashboards, admin panels, and business tools tailored to your workflow",
      problem: "Juggling multiple tools that don't communicate with each other",
      solution: "Unified internal platforms with custom dashboards, automation, and real-time insights",
      outcome: "Centralized operations, 60% faster decision-making",
      features: ["Custom Dashboards", "Admin Panels", "Data Visualization", "Real-time Analytics"]
    },
    {
      id: 4,
      title: "Backend Systems",
      description: "Robust APIs, databases, and server infrastructure that scale with your business",
      problem: "Unreliable systems that break under load or can't scale",
      solution: "Enterprise-grade backend systems with RESTful APIs, optimized databases, and cloud infrastructure",
      outcome: "99.9% uptime, handles 10x traffic without breaking",
      features: ["RESTful APIs", "Database Design", "Cloud Infrastructure", "Scalable Architecture"]
    }
  ],
  
  projects: [
    {
      id: 1,
      title: "Food Delivery Platform",
      description: "A complete food ordering system with real-time order tracking and payment integration",
      techStack: ["React", "Node.js", "MongoDB", "Stripe"],
      problem: "Local restaurants needed an online ordering system to compete with major delivery apps",
      solution: "Built a custom platform with menu management, real-time tracking, and integrated payments",
      outcome: "Processed 500+ orders in first month, 40% lower fees than competitors",
      image: "https://t3.ftcdn.net/jpg/02/28/28/16/240_F_228281605_zX4rGhhAX6fNkBgx7dp1IXu8XMnhCl01.jpg"
    },
    {
      id: 2,
      title: "College Event Management System",
      description: "Platform for organizing and managing campus events with registration and analytics",
      techStack: ["React", "Django", "PostgreSQL", "AWS"],
      problem: "Manual event registration leading to data chaos and poor attendance tracking",
      solution: "Developed a centralized platform with automated registration, QR code check-ins, and analytics",
      outcome: "Managed 50+ events, 5000+ registrations, 90% reduction in admin time",
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&q=80"
    },
    {
      id: 3,
      title: "Hotel Management API",
      description: "Backend system for hotel bookings, room management, and guest services",
      techStack: ["Django", "PostgreSQL", "Redis", "REST API"],
      problem: "Legacy system couldn't handle concurrent bookings and frequently crashed",
      solution: "Built scalable REST APIs with caching, concurrent booking handling, and real-time availability",
      outcome: "Handled 100+ simultaneous bookings, 99.9% uptime, 3x faster response times",
      image: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80"
    },
    {
      id: 4,
      title: "AI Personal Assistant",
      description: "Intelligent desktop assistant for task automation and information retrieval",
      techStack: ["Python", "OpenAI API", "Electron", "SQLite"],
      problem: "Switching between multiple apps for daily tasks was time-consuming",
      solution: "Created an AI-powered assistant that automates tasks, manages schedules, and provides instant information",
      outcome: "Saved 2+ hours daily, automated 80% of repetitive tasks",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80"
    }
  ],
  
  experience: [
    {
      id: 1,
      company: "KPMG Global Services",
      role: "Software Engineer",
      duration: "1+ Year",
      description: "Worked with enterprise systems and real-world business processes, gaining experience in building reliable and scalable solutions for Fortune 500 clients",
      achievements: [
        "Built enterprise-grade applications serving 10,000+ users",
        "Developed RESTful APIs and microservices architecture",
        "Collaborated with cross-functional teams on large-scale projects",
        "Implemented best practices for code quality and system reliability"
      ]
    },
    {
      id: 2,
      company: "Freelance & Personal Projects",
      role: "Full Stack Developer",
      duration: "2+ Years",
      description: "Delivered custom solutions for clients ranging from startups to established businesses",
      achievements: [
        "Built 10+ production applications across various domains",
        "Managed end-to-end project delivery from ideation to deployment",
        "Specialized in AI integration and automation solutions",
        "Maintained 95%+ client satisfaction rate"
      ]
    }
  ],
  
  skills: {
    languages: [
      { name: "JavaScript", proficiency: 90 },
      { name: "Python", proficiency: 90 },
      { name: "Java", proficiency: 85 },
      { name: "SQL", proficiency: 85 }
    ],
    frameworks: [
      { name: "React", proficiency: 95 },
      { name: "Node.js", proficiency: 90 },
      { name: "Django", proficiency: 85 },
      { name: "FastAPI", proficiency: 80 }
    ],
    tools: [
      { name: "Git", proficiency: 90 },
      { name: "Docker", proficiency: 80 },
      { name: "AWS", proficiency: 75 },
      { name: "MongoDB", proficiency: 85 },
      { name: "PostgreSQL", proficiency: 85 }
    ]
  },
  
  about: {
    title: "Turning Complex Problems Into Simple Solutions",
    story: "I'm a Computer Science Engineer with over a year of experience at KPMG Global Services, where I worked on enterprise systems serving thousands of users. My journey in tech started with a simple question: How can we make businesses more efficient?",
    mission: "Today, I help businesses save time and money by building AI-powered automation, modern web applications, and scalable backend systems. I don't just write code—I solve real business problems.",
    approach: "My approach is simple: understand your business, identify bottlenecks, and build solutions that actually work. Whether it's automating repetitive tasks, creating a customer-facing application, or building robust APIs, I focus on delivering measurable results.",
    education: "B.Tech in Computer Science Engineering from SSAHE University",
    location: "Available for remote projects worldwide",
    mission1: "Building solutions that save businesses time and money while delivering measurable results"
  },
  
  testimonials: [
    {
      id: 1,
      name: "Sarah Mitchell",
      role: "CEO, TechStart Solutions",
      company: "TechStart Solutions",
      content: "The AI chatbot solution reduced our customer support workload by 60%. The ROI was visible within the first month. Highly professional and delivers exactly what's promised.",
      rating: 5
    },
    {
      id: 2,
      name: "Rajesh Kumar",
      role: "Operations Manager",
      company: "LogiFlow Industries",
      content: "We needed a custom dashboard to track our operations in real-time. The solution delivered was beyond our expectations—intuitive, fast, and exactly what we needed. Our decision-making speed has improved dramatically.",
      rating: 5
    },
    {
      id: 3,
      name: "Emily Chen",
      role: "Founder",
      company: "GreenLeaf Organics",
      content: "Our old website was slow and outdated. The new React-based site loads 3x faster and our conversion rate increased by 45%. The attention to detail and ongoing support has been exceptional.",
      rating: 5
    }
  ],
  
  contact: {
    email: "aitechsolutions.business@gmail.com",
    linkedin: "https://www.linkedin.com/in/ullasgowda3523/",
    whatsapp: "https://wa.me/918660801877",
    calendlyUrl: "https://calendly.com/aitechsolutions-business/30min",
    googleFormUrl: "https://forms.gle/VLXSRvf7sHuFMBGq9"
  },
  
  instructions: {
    googleForm: "Replace '#google-form-placeholder' with your actual Google Form embed URL. Get it from Google Forms > Send > Embed HTML > Copy the src URL",
    calendly: "Replace '#calendly-placeholder' with your Calendly scheduling link. Get it from Calendly > Your Link > Copy Link"
  }
};